/** Nozl's own accent (neon lime). Companies can keep it or choose their own. */
export const DEFAULT_BRAND = "#c6ff00";
export const NOZL = { charcoal: "#0b0b0b", lime: "#c6ff00", slate: "#2e2e2e", lightGrey: "#e5e5e5", white: "#ffffff" };
const rgb = (hex) => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
const toHex = (c) => "#" + c.map((v) => Math.round(Math.min(255, Math.max(0, v))).toString(16).padStart(2, "0")).join("");
export function mix(a, b, t) {
    const x = rgb(a), y = rgb(b);
    return toHex(x.map((v, i) => v + (y[i] - v) * t));
}
function luminance(hex) {
    const [r, g, b] = rgb(hex).map((v) => {
        const s = v / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}
export function contrast(a, b) {
    const la = luminance(a), lb = luminance(b);
    return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}
export function isHex(v) {
    return typeof v === "string" && /^#[0-9a-f]{6}$/i.test(v);
}
/**
 * Any brand colour can be used as it is: the text on it is chosen to be readable (charcoal on light colours like
 * Nozl lime, white on dark ones). Kept for the branding API, which used to darken pale colours.
 */
export function readableBrand(input) {
    return { brand: isHex(input) ? input.toLowerCase() : DEFAULT_BRAND, adjusted: false };
}
/** The colour to write on top of a fill: charcoal or white, whichever reads better. */
export function inkOn(fill) {
    return contrast(fill, NOZL.charcoal) >= contrast(fill, "#ffffff") ? NOZL.charcoal : "#ffffff";
}
/** Move a colour towards black (or white) until it reaches the contrast asked for against `bg`. */
function towards(c, bg, target) {
    const to = luminance(bg) > 0.4 ? "#000000" : "#ffffff";
    let out = c;
    for (let i = 0; i < 24 && contrast(out, bg) < target; i++)
        out = mix(out, to, 0.07);
    return out;
}
const RADII = { rounded: ["8px", "12px", "20px"], soft: ["4px", "8px", "11px"], square: ["2px", "3px", "4px"] };
/** CSS custom properties for a company's branding, for one theme. */
export function brandTheme(branding, theme) {
    const { brand } = readableBrand(branding.brand ?? DEFAULT_BRAND);
    const deep = mix(brand, "#000000", 0.35);
    const light = mix(brand, "#ffffff", 0.25);
    const panel = theme === "dark" ? "#161616" : "#ffffff";
    const onBrand = inkOn(brand);
    // Brand colour used as text or a thin line on the page: darkened (or lightened) just enough to read.
    const text = towards(brand, panel, 4.5);
    const line = towards(brand, panel, 2.6);
    const [sm, md, lg] = RADII[branding.corners ?? "rounded"];
    // "canvas" (the default): the page title sits on the page itself and controls are white pills. The coloured
    // banners put white, frosted controls on the brand colour. Banner controls read these --bn-* values.
    const style = branding.banner ?? "canvas";
    const onColour = style === "gradient" || style === "solid";
    const banner = style === "solid" ? brand
        : style === "minimal" ? "var(--surface-panel)"
            : style === "canvas" ? "transparent"
                : `linear-gradient(118deg, ${NOZL.charcoal} 0%, ${NOZL.slate} 62%, ${mix(NOZL.slate, brand, 0.25)} 100%)`;
    const bn = onColour
        ? { "--bn-glass": "rgba(255,255,255,.12)", "--bn-glass-hover": "rgba(255,255,255,.2)", "--bn-line": "rgba(255,255,255,.26)", "--bn-sub": "rgba(255,255,255,.82)",
            "--bn-shadow": "none", "--bn-on": brand, "--bn-on-ink": onBrand, "--bn-strong": brand, "--bn-strong-ink": onBrand, "--bn-blur": "blur(10px)", "--banner-overlap": "56px", "--bn-aurora": "visible", "--bn-ring": "#ffffff" }
        : { "--bn-glass": "var(--surface-panel)", "--bn-glass-hover": "var(--surface-sunken)", "--bn-line": "var(--line)", "--bn-sub": "var(--ink-muted)",
            "--bn-shadow": "0 1px 2px rgba(11,11,11,.05)", "--bn-on": NOZL.charcoal, "--bn-on-ink": "#ffffff",
            "--bn-strong": brand, "--bn-strong-ink": onBrand, "--bn-blur": "none", "--banner-overlap": style === "minimal" ? "24px" : "0px", "--bn-aurora": "hidden" };
    // The menu and top bar (30/10/26, Jamie: "tone down from jet black … accents of the company colour … the top bar and
    // the side bar need to not completely merge"): a charcoal tinted with the brand, the top bar a step lighter, a quiet
    // line between them. The accent marks where you are.
    const accent = readableBrand(branding.accent ?? mix(brand, "#ffffff", 0.3)).brand;
    const chrome = mix(brand, "#17191c", 0.88);
    return {
        ...bn,
        "--accent": accent,
        "--on-accent": inkOn(accent),
        "--chrome": chrome,
        "--chrome-top": mix(brand, "#22252a", 0.9),
        "--chrome-raise": mix(brand, "#2a2d33", 0.84),
        "--chrome-line": mix(brand, "#30333a", 0.72),
        "--chrome-on": mix(brand, "#2a2d33", 0.62),
        "--brand": brand,
        "--brand-deep": deep,
        "--brand-light": light,
        "--brand-tint": theme === "dark" ? mix(brand, panel, 0.82) : mix(brand, "#ffffff", 0.84),
        "--brand-text": text,
        "--brand-line": line,
        "--on-brand": onBrand,
        "--banner-bg": banner,
        "--banner-ink": onColour ? "#ffffff" : "var(--ink)",
        "--banner-border": style === "minimal" ? "1px solid var(--line)" : "0",
        "--radius-sm": sm,
        "--radius-md": md,
        "--radius-lg": lg,
    };
}
